import React, { FunctionComponent } from 'react';
import { find, is } from 'ramda';
import { FormattedMessage, injectIntl, IntlShape } from 'react-intl';
import { IBindingAction, IBindingCallback1 } from '../../common/models/callback';
import CustomPopover from '../CustomPopover';
import Icon from '../Icon';
import { concat } from '../../common/helpers/arrayHelper';
import { IFormattedMessage } from '../../common/models/common/IFormattedMessage';
import styles from './styles.module.scss';
import Checkbox from '../Checkbox';

interface IProps {
  error?: boolean;
  intl: IntlShape;
  value?: string;
  options: Array<{ value: string; label: string | IFormattedMessage }>;
  defaultValueIntl?: string;
  defaultValueText?: string;
  disabled?: boolean;
  enableAction?: boolean;
  variant?: 'select' | 'radio';
  triggerVariant?: 'bordered' | 'underlined';
  label?: IFormattedMessage;
  placement?: 'bottom-start' | 'top-end';
  onChange: IBindingCallback1<string>;
}

const CustomSelect: FunctionComponent<IProps> = ({
  intl,
  value,
  error,
  options,
  onChange,
  disabled = false,
  enableAction = false,
  defaultValueText,
  defaultValueIntl,
  label: labelIntl,
  variant = 'select',
  triggerVariant = 'bordered',
  placement = 'bottom-start'
}) => {
  const selectedOption = find(({ value: optionValue }) => optionValue === value, options);
  const selectedLabel = (!selectedOption || is(String, selectedOption?.label))
    ? selectedOption?.label
    : <FormattedMessage {...(selectedOption.label as IFormattedMessage)} />;

  const Trigger = () => (
    <div className={concat(
      triggerVariant === 'bordered' ? 'popup-trigger' : 'text-dropdown-trigger',
      styles.trigger,
      disabled ? 'trigger-disabled' : '',
      enableAction ? 'cursor-pointer' : '',
      styles[triggerVariant],
      error && styles.error
    )}
    >
      <span className="flex-grow-1 overflow-ellipsis">
        {selectedOption
          ? selectedLabel
          : (
            defaultValueIntl && (
              <span className={styles.placeholder}>
                <FormattedMessage id={defaultValueIntl} defaultMessage={defaultValueText} />
              </span>
            ))}
      </span>
      <Icon icon="chevronDown" className="popup-chevron" />
    </div>
  );

  const onChangeOption = (optionValue: string, hidePopover?: IBindingAction) => {
    onChange(optionValue);
    if (hidePopover && variant !== 'radio') {
      hidePopover();
    }
  };

  const Options = ({ hidePopover }: { hidePopover?: IBindingAction }) => (
    <div className={`${styles[variant]} ${styles.optionsContainer}`}>
      {options.map(({ label, value: optionValue }) => {
        const labelElement = is(String, label) ? label : <FormattedMessage {...(label as IFormattedMessage)} />;
        return (
          <div
            key={optionValue}
            role="presentation"
            onClick={() => onChangeOption(optionValue, hidePopover)}
            className={`${styles.option} ${optionValue === value ? styles.active : ''} overflow-ellipsis`}
          >
            {variant === 'radio'
              ? <Checkbox label={label} radio checked={optionValue === value} />
              : labelElement}
          </div>
        );
      })}
    </div>
  );

  return (
    <div className={styles.selectContainer}>
      {labelIntl && (
        <div className={styles.label}>
          <FormattedMessage {...labelIntl} />
        </div>
      )}
      <div className="popup-wrapper">
        {(disabled && !enableAction)
          ? <Trigger />
          : (
            <CustomPopover
              placement={placement}
              trigger={<Trigger />}
              popoverClassName={styles.selectBody}
              rootClose
            >
              <Options />
            </CustomPopover>
          )}
      </div>
      <select
        className={`${styles.mobileSelect} ${error ? styles.error : ''}`}
        value={value}
        onChange={({ currentTarget }) => onChange(currentTarget.value)}
      >
        {!value && <option hidden disabled selected> </option>}
        {options.map(({ value: optValue, label }) => (
          <option key={optValue} value={optValue}>
            {is(String, label) ? label : intl.formatMessage(label as IFormattedMessage)}
          </option>
        ))}
      </select>
    </div>
  );
};

export default injectIntl(CustomSelect);
